import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { format } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { FruitifyContainer } from "../../../shared-components/Containers";
import GeneralLoading from "../../../shared-components/Loading";
import { useAuthenticationStore } from "../../../stores/auth";
import { useApiClient } from "../../../api";

const ModeratedAnswersHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticationStore();
  const ApiClient = useApiClient();

  const [selectedEducationLevel, setSelectedEducationLevel] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [moderatedAnswers, setModeratedAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const moderator = user?.info?.moderator;
  const educationLevels = moderator?.educationLevels || [];
  const subjects = moderator?.subjects || [];

  useEffect(() => {
    if (educationLevels.length === 1) {
      setSelectedEducationLevel(educationLevels[0].id);
    }
    if (subjects.length === 1) {
      setSelectedSubject(subjects[0].id);
    }
  }, [educationLevels, subjects]);

  useEffect(() => {
    const fetchModeratedAnswers = async () => {
      if (selectedEducationLevel || selectedSubject) {
        setLoading(true);
        setError(null);
        try {
          const answers = await ApiClient.moderator.getModeratedAnswers({
            educationLevel: selectedEducationLevel || undefined,
            subject: selectedSubject || undefined,
          });
          setModeratedAnswers(answers || []);
        } catch (err) {
          setError(err.message);
          enqueueSnackbar(`Error loading moderated answers: ${err.message}`, {
            variant: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    };
    fetchModeratedAnswers();
  }, [selectedEducationLevel, selectedSubject]);

  const getAnswerIdentifier = (answer) => {
    // Decode base64 relay global ID to extract numeric ID
    // Format: "AnswerNode:43" encoded as base64
    try {
      const decoded = atob(answer.id);
      const numericId = decoded.split(":")[1] || decoded;
      return `Student ${numericId}`;
    } catch (e) {
      // Fallback if decoding fails
      return `Student ${answer.id}`;
    }
  };

  return (
    <FruitifyContainer>
      <Typography variant="h4" gutterBottom>
        Moderated Answers History
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filter by Education Level and Subject
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="education-level-label">
                Education Level
              </InputLabel>
              <Select
                labelId="education-level-label"
                id="education-level-select"
                value={selectedEducationLevel}
                onChange={(e) => setSelectedEducationLevel(e.target.value)}
                label="Education Level"
              >
                <MenuItem value="">All</MenuItem>
                {educationLevels.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="subject-label">Subject</InputLabel>
              <Select
                labelId="subject-label"
                id="subject-select"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                label="Subject"
              >
                <MenuItem value="">All</MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <GeneralLoading />
      ) : error ? (
        <Alert severity="error">Error loading moderated answers: {error}</Alert>
      ) : moderatedAnswers.length === 0 ? (
        <Alert severity="info">
          No moderated answers found. Start moderating to see your history here.
        </Alert>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Moderated Answers ({moderatedAnswers.length})
            </Typography>
            <Stack spacing={2}>
              {moderatedAnswers.map((answer) => (
                <Card
                  key={answer.id}
                  variant="outlined"
                  sx={{
                    "&:hover": {
                      boxShadow: 2,
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => navigate(`/moderator/answer/${answer.id}`)}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={1}
                        >
                          <Typography variant="body1" fontWeight="medium">
                            {getAnswerIdentifier(answer)}
                          </Typography>
                          <Chip
                            label={`${answer.moderatorFeedback?.mark || 0} marks`}
                            size="small"
                            color="primary"
                          />
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="medium"
                          mb={0.5}
                        >
                          Question:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            mb: 1,
                          }}
                        >
                          {answer.question.questionText}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="medium"
                          mb={0.5}
                        >
                          Answer:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            mb: 1,
                          }}
                        >
                          {answer.answer || "No answer provided"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          mt={1}
                          flexWrap="wrap"
                        >
                          <Chip
                            label={answer.question.subject.name}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={answer.question.educationLevel.name}
                            size="small"
                            variant="outlined"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ alignSelf: "center" }}
                          >
                            Last moderated at:{" "}
                            {answer.moderatorFeedback?.approvedAt
                              ? format(
                                  new Date(answer.moderatorFeedback.approvedAt),
                                  "MMM dd, yyyy HH:mm",
                                )
                              : "N/A"}
                          </Typography>
                        </Stack>
                      </Box>
                      <Tooltip title="Edit moderation">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/moderator/answer/${answer.id}`);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </FruitifyContainer>
  );
};

export default ModeratedAnswersHistory;
